import React from "react";
import { Lang } from "../i18n/languages";

// One flag per app language, drawn here rather than as emoji (no Apple emoji
// in product UI): they look the same on every phone and browser. English is
// the US flag and Portuguese the Brazilian one, matching the app's American
// English and Brazilian wording (src/i18n/README.md).

const W = 30;
const H = 22;

const stripes = (colors: string[], vertical = false) =>
  colors.map((c, i) =>
    vertical ? (
      <rect key={i} x={(W / colors.length) * i} y={0} width={W / colors.length + 0.1} height={H} fill={c} />
    ) : (
      <rect key={i} x={0} y={(H / colors.length) * i} width={W} height={H / colors.length + 0.1} fill={c} />
    ),
  );

const FLAGS: Record<Lang, React.ReactNode> = {
  en: (
    <>
      {stripes(Array.from({ length: 13 }, (_, i) => (i % 2 ? "#fff" : "#c8102e")))}
      <rect width={13} height={H * (7 / 13)} fill="#0a3161" />
      {Array.from({ length: 12 }, (_, i) => (
        <circle key={i} cx={2 + (i % 4) * 3 + (Math.floor(i / 4) % 2) * 1.5} cy={2.2 + Math.floor(i / 4) * 3.6} r={0.7} fill="#fff" />
      ))}
    </>
  ),
  fr: stripes(["#0055a4", "#fff", "#ef4135"], true),
  es: (
    <>
      {stripes(["#aa151b", "#f1bf00", "#f1bf00", "#aa151b"])}
      <rect x={7} y={8} width={4} height={6} rx={1} fill="#aa151b" />
      <rect x={6} y={8} width={1} height={6} fill="#c8a000" />
      <rect x={11} y={8} width={1} height={6} fill="#c8a000" />
    </>
  ),
  de: stripes(["#000", "#dd0000", "#ffce00"]),
  it: stripes(["#009246", "#fff", "#ce2b37"], true),
  ja: (
    <>
      <rect width={W} height={H} fill="#fff" />
      <circle cx={W / 2} cy={H / 2} r={6.3} fill="#bc002d" />
    </>
  ),
  el: (
    <>
      {stripes(Array.from({ length: 9 }, (_, i) => (i % 2 ? "#fff" : "#0d5eaf")))}
      <rect width={(H / 9) * 5} height={(H / 9) * 5} fill="#0d5eaf" />
      <rect x={(H / 9) * 2} width={H / 9} height={(H / 9) * 5} fill="#fff" />
      <rect y={(H / 9) * 2} width={(H / 9) * 5} height={H / 9} fill="#fff" />
    </>
  ),
  pt: (
    <>
      <rect width={W} height={H} fill="#009c3b" />
      <path d={`M${W / 2} 2.6 L${W - 3} ${H / 2} L${W / 2} ${H - 2.6} L3 ${H / 2} Z`} fill="#ffdf00" />
      <circle cx={W / 2} cy={H / 2} r={5} fill="#002776" />
      <path d={`M${W / 2 - 4.9} ${H / 2 - 0.6} Q${W / 2} ${H / 2 - 2.6} ${W / 2 + 4.9} ${H / 2 + 1.2}`} stroke="#fff" strokeWidth={1} fill="none" />
    </>
  ),
  nl: stripes(["#ae1c28", "#fff", "#21468b"]),
};

export default function LanguageFlag({ lang }: { lang: Lang }) {
  const clip = `lang-flag-${lang}`;
  return (
    <svg className="lang-flag" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <clipPath id={clip}>
        <rect width={W} height={H} rx={4} />
      </clipPath>
      <g clipPath={`url(#${clip})`}>{FLAGS[lang]}</g>
      <rect x={0.25} y={0.25} width={W - 0.5} height={H - 0.5} rx={3.8} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth={0.5} />
    </svg>
  );
}
